import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Alert } from 'reactstrap';

import { ErrorResponse, Spinner } from '~components';
import Button from '~components/Button';
import Icon, { faCheckCircle } from '~components/icon';

import { getErrorMessage } from '~libs/axiosWrapper';

import { InputProps } from '~components/form-fields/props';

import { getTP1ClientPackageSchedulesUrl } from '~constants/routes-tp1';

import { LevelMovePayload, useLevelMoveMutation } from '~support-at-home/actions/packageSchedules';

type ComponentProps = InputProps;

// Defensively ensure a redirect target is an absolute URL. `REACT_APP_TPONE_URL` is sometimes
// configured without a protocol (e.g. just "localhost") in dev — `window.location.replace`
// would otherwise interpret that as a path segment relative to the current page.
const ensureAbsoluteUrl = (url: string): string => {
  if (/^https?:\/\//i.test(url)) return url;
  return `http://${url}`;
};

const Execute: React.FC<ComponentProps> = ({ value }) => {
  const { scheduleId: scheduleIdRaw, clientId: clientIdRaw } = useParams<{ scheduleId: string; clientId: string }>();
  const scheduleId = Number(scheduleIdRaw);
  const clientId = Number(clientIdRaw);

  const [levelMove, { isLoading, isError, isSuccess, error, data }] = useLevelMoveMutation();

  const formValues = value as Record<string, any>;

  useEffect(() => {
    const payload: LevelMovePayload = {
      new_package_id: Number(formValues?.new_package_id),
      changeover_date: formValues?.changeover_date ?? '',
      case_manager_id: Number(formValues?.case_manager_id),
      repeat_appointment_ids: (formValues?.repeat_appointment_ids ?? []) as number[],
      fixed_appointment_ids: (formValues?.fixed_appointment_ids ?? []) as number[],
    };

    levelMove({ scheduleId, payload })
      .unwrap()
      .catch(() => {
        // Error is displayed via isError state
      });
  }, []);

  const onFinish = () => {
    window.location.replace(ensureAbsoluteUrl(getTP1ClientPackageSchedulesUrl(clientId)));
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner loading={isLoading} />
        <p className="mt-3 text-muted">Executing level move...</p>
      </div>
    );
  }

  if (isError) {
    return <ErrorResponse message={getErrorMessage(error)} />;
  }

  if (isSuccess && data) {
    return (
      <>
        <Alert color="success" className="w-100 mb-3">
          <Icon icon={faCheckCircle} className="me-2" />
          <b>Level Move Complete</b>
          <div className="mt-2">
            <p className="mb-1">
              New schedule ID: <strong>{data.new_schedule_id}</strong>
            </p>
            <p className="mb-0">
              <strong>{data.moved_appointment_count}</strong> appointment
              {data.moved_appointment_count !== 1 ? 's' : ''} moved
            </p>
          </div>
        </Alert>
        <div className="d-flex justify-content-end">
          <Button color="primary" onClick={onFinish} data-cy="level-move-finish-button">
            Finish
          </Button>
        </div>
      </>
    );
  }

  return null;
};

export default Execute;
